import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, RouterProvider, ScrollRestoration, Outlet } from "react-router";
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import { EmptyState, PageLoader } from "@/components/ui";
import { useI18n } from "@/lib/i18n";
import Home from "@/pages/Home";

// Home is in the main bundle; every other screen is loaded on demand.
const CategoriesPage = lazy(() => import("@/pages/Categories").then((m) => ({ default: m.CategoriesPage })));
const CategoryPage = lazy(() => import("@/pages/Categories").then((m) => ({ default: m.CategoryPage })));
const PackPage = lazy(() => import("@/pages/PackPage"));
const SearchPage = lazy(() => import("@/pages/Search"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const CartPage = lazy(() => import("@/pages/Cart"));
const CheckoutPage = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const OrdersPage = lazy(() => import("@/pages/Orders").then((m) => ({ default: m.OrdersPage })));
const OrderDetailPage = lazy(() => import("@/pages/Orders").then((m) => ({ default: m.OrderDetailPage })));
const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountPage })));
const AddressEditorPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AddressEditorPage })));
const LoginPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.RegisterPage })));
const VerifyPhonePage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.VerifyPhonePage })));
const ForgotPasswordPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.ForgotPasswordPage })));
const PrivacyPage = lazy(() => import("@/pages/Privacy"));
const PhotoCreditsPage = lazy(() => import("@/pages/PhotoCredits"));

function NotFound() {
  const { t } = useI18n();
  return <EmptyState icon={<span className="text-3xl">🧭</span>} title={t("common.notFound")} action={{ label: t("common.goHome"), to: "/" }} />;
}

function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

const page = (el: ReactNode) => <Suspense fallback={<PageLoader />}>{el}</Suspense>;
const protect = (el: ReactNode) => page(<RequireAuth>{el}</RequireAuth>);

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        element: <Layout />,
        children: [
          { path: "/", element: <Home /> },
          { path: "/categories", element: page(<CategoriesPage />) },
          { path: "/categories/:slug", element: page(<CategoryPage />) },
          { path: "/search", element: page(<SearchPage />) },
          { path: "/products/:id", element: page(<ProductPage />) },
          { path: "/packs/:id", element: page(<PackPage />) },
          { path: "/cart", element: page(<CartPage />) },
          { path: "/checkout", element: protect(<CheckoutPage />) },
          { path: "/order-success/:orderNumber", element: protect(<OrderSuccess />) },
          { path: "/orders", element: protect(<OrdersPage />) },
          { path: "/orders/:orderNumber", element: protect(<OrderDetailPage />) },
          { path: "/account", element: page(<AccountPage />) },
          { path: "/account/addresses/new", element: protect(<AddressEditorPage />) },
          { path: "/account/addresses/:id", element: protect(<AddressEditorPage />) },
          { path: "/login", element: page(<LoginPage />) },
          { path: "/register", element: page(<RegisterPage />) },
          { path: "/register/verify", element: page(<VerifyPhonePage />) },
          { path: "/forgot-password", element: page(<ForgotPasswordPage />) },
          { path: "/confidentialite", element: page(<PrivacyPage />) },
          { path: "/credits-photos", element: page(<PhotoCreditsPage />) },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
